import { LayerData } from "@components/map//utils/mapLayers"
import { useSetMapBoundsV3, type MapBoundsV3Input } from "@components/map//utils/useSetMapBounds"
import ErrorMessageMap from "@components/map/mapComponents/ErrorMessageMap"
import { MapButtons } from "@components/map/mapComponents/MapButtons"
import useAddLayersDataToMap from "@components/map/utils/useAddLayersDataToMap"
import { FiltersPanelMemo } from "@components/map/windyMap/MapComponent"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { getMapFiltersPanelState } from "@redux/features/mapFiltersPanelState/mapFiltersPanelStateSlice"
import { getPanelState } from "@redux/features/mapSidePanelControl/mapSidePanelControlSlice"
import { getSelectedVessel } from "@redux/features/selectedVessel/selectedVesselSlice"
import { FacilitiesFiltersStorageKey } from "@sop/MapPage/MapFiltersFacilitiesContent"
import { MapFiltersFishStorageKey } from "@sop/MapPage/MapFiltersFishContent"
import { MapFiltersOilStorageKey } from "@sop/MapPage/MapFiltersOilContent"
import { useQueryClient } from "@tanstack/react-query"
import { common } from "@utils/common"
import ErrorBoundary from "@utils/errorBoundary/ErrorBoundary"
import type { LatLngBounds } from "leaflet"
import { Dispatch, ReactElement, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { MdClear, MdSearch } from "react-icons/md"
import { RegularMapType } from "./regularMap"
import "./regularMap.css"
import useInitRegularMap from "./useInitRegularMap"

type MapData<T> = {
	layersData: LayerData[]
	onSearchChange?: (searchString: string) => void
	searchString?: string | null
	setSearchString?: Dispatch<SetStateAction<string | null>>
	inputsForBoundCalc?: T
	fitBounds?: LatLngBounds
	customerName: string
	mapDivId?: string
	mapWrapperClassName?: string
	minZoom?: number
	maxZoom?: number
	facilitiesFiltersStorageKey?: FacilitiesFiltersStorageKey
	oilFiltersStorageKey?: MapFiltersOilStorageKey
	fishFiltersStorageKey?: MapFiltersFishStorageKey
}

export default function RegularMapComponent3(props: MapData<MapBoundsV3Input | undefined>) {
	return (
		<ErrorBoundary fallback={ErrorMessageMap}>
			<RegularMapComponentBodyBounds3 {...props} />
		</ErrorBoundary>
	)
}
function RegularMapComponentBodyBounds3({
	layersData,
	onSearchChange,
	searchString,
	setSearchString,
	inputsForBoundCalc,
	fitBounds,
	customerName,
	mapDivId = "regularMap",
	mapWrapperClassName,
	minZoom,
	maxZoom,
	facilitiesFiltersStorageKey,
	oilFiltersStorageKey,
	fishFiltersStorageKey,
}: MapData<MapBoundsV3Input | undefined>): ReactElement {
	const mapFiltersPanelState = useAppSelector(getMapFiltersPanelState)

	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const { isOpen } = useAppSelector(getPanelState)
	const [regularMap, setRegularMap] = useState<RegularMapType>()
	const [mapRef, setMapRef] = useState<HTMLDivElement | null>(null)

	useInitRegularMap(dispatch, queryClient, setRegularMap, t, regularMap, mapDivId, {
		minZoom,
		maxZoom,
	})

	useEffect(() => {
		if (regularMap === undefined) {
			return
		}

		const timeouts: ReturnType<typeof setTimeout>[] = []
		const animationFrames: number[] = []
		const triggerResize = () => {
			animationFrames.push(
				requestAnimationFrame(() => {
					regularMap.resizemap()
				})
			)
		}

		// Run a few passes so Leaflet also catches animated width changes.
		triggerResize()
		for (const delay of [100, 250, 500]) {
			timeouts.push(
				setTimeout(() => {
					triggerResize()
				}, delay)
			)
		}

		return () => {
			for (const timeoutId of timeouts) {
				clearTimeout(timeoutId)
			}
			for (const animationFrameId of animationFrames) {
				cancelAnimationFrame(animationFrameId)
			}
		}
	}, [regularMap, isOpen])

	// useAssignRegularMapDiv()
	const selectedVessel = useAppSelector(getSelectedVessel)
	const plantData = common.plantDataFromOwnerAndName.get(
		`${customerName?.toLowerCase()}/${selectedVessel.name?.toLowerCase()}`
	)
	useAddLayersDataToMap(layersData, undefined, { map: regularMap }, customerName, plantData)
	useSetMapBoundsV3(regularMap, inputsForBoundCalc)

	useEffect(() => {
		if (regularMap === undefined || mapRef === null) {
			return
		}

		const triggerResize = () => {
			requestAnimationFrame(() => {
				regularMap.applyConstraints()
				regularMap.resizemap()
			})
		}

		triggerResize()

		if (typeof ResizeObserver === "undefined") {
			return
		}

		const observer = new ResizeObserver(() => {
			triggerResize()
		})

		observer.observe(mapRef)

		return () => {
			observer.disconnect()
		}
	}, [regularMap, mapRef])

	return (
		<div className={`relative h-full w-full ${mapWrapperClassName ?? ""}`}>
			{/* REGULAR MAP CONTAINER */}
			<div
				ref={(newRef) => setMapRef(newRef)}
				id={mapDivId}
				className={`h-full w-full`}
			/>

			{/* GLOBAL FILTER */}
			{onSearchChange !== undefined && setSearchString !== undefined ? (
				<div
					id={"globalFilter"}
					className="absolute top-3.5 left-16 z-900 inline-block rounded-md border-2 border-[rgba(0,0,0,0.2)]"
				>
					<div className="absolute inset-x-1 inset-y-1 h-6 w-6">
						<MdSearch className={`h-6 w-6`} />
					</div>
					<input
						className="text-menu shadow-card hover:border-alert-focus focus:border-alert-focus h-8 rounded-sm bg-white px-8 focus:border-1 focus:outline-hidden"
						value={searchString ?? ""}
						onChange={(e) => {
							setSearchString(e.target.value)
							onSearchChange(e.target.value)
						}}
						placeholder={t("SEARCH VESSEL BY NAME")}
					/>
					{searchString !== "" && searchString !== undefined && (
						<div
							className="text-gray-666 absolute inset-y-1 right-1 h-6 w-6 cursor-pointer"
							onClick={() => {
								setSearchString("")
								onSearchChange("")
							}}
						>
							<MdClear className={`h-6 w-6`} />
						</div>
					)}
				</div>
			) : null}
			{/* MAP BUTTONS */}
			<MapButtons
				map={regularMap}
				fitBounds={fitBounds}
			/>

			{/* FILTERS PANEL */}
			<FiltersPanelMemo
				dispatch={dispatch}
				isOpen={mapFiltersPanelState.isOpen}
				mapRef={mapRef}
				plantData={plantData}
				facilitiesStorageKey={facilitiesFiltersStorageKey}
				oilFiltersStorageKey={oilFiltersStorageKey}
				fishFiltersStorageKey={fishFiltersStorageKey}
			/>

			{/* MAPTILE ATTRIBUTION */}
			<a
				id="maptilerLink"
				href="https://www.maptiler.com"
				className="absolute bottom-4 left-4 z-999"
			>
				<img
					src="https://api.maptiler.com/resources/logo.svg"
					alt="MapTiler logo"
				/>
			</a>
		</div>
	)
}
